import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ImageUploader from '../components/ImageUploader';
import { useImageUpload } from '../hooks/useImageUpload';

const empty = { name:'', jobTitle:'', location:'Japan', story:'', imageUrl:'', imagePublicId:'', isActive:true };

export default function SuccessStories() {
  const [stories, setStories]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modalOpen, setModalOpen]     = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId]       = useState(null);
  const [form, setForm]               = useState(empty);
  const [editing, setEditing]         = useState(false);
  const { upload, uploading, preview, setPreview } = useImageUpload('/upload/story-image');

  const fetchStories = async () => { setLoading(true); const {data}=await API.get('/success-stories'); setStories(data); setLoading(false); };
  useEffect(()=>{ fetchStories(); },[]);

  const openCreate = () => { setForm(empty); setPreview(''); setEditing(false); setModalOpen(true); };
  const openEdit   = (s) => { setForm(s); setPreview(s.imageUrl||''); setEditing(true); setModalOpen(true); };

  const handleImageUpload = async (file) => { const r=await upload(file); if(r) setForm(f=>({...f,imageUrl:r.imageUrl,imagePublicId:r.publicId})); };
  const handleImageRemove = () => { setPreview(''); setForm(f=>({...f,imageUrl:'',imagePublicId:''})); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await API.put(`/success-stories/${form._id}`, form); toast.success('Story updated!'); }
      else         { await API.post('/success-stories', form);             toast.success('Story added!'); }
      setModalOpen(false); fetchStories();
    } catch(err) { toast.error(err.response?.data?.message||'Something went wrong!'); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/success-stories/${deleteId}`); toast.success('Story deleted!'); setConfirmOpen(false); fetchStories(); }
    catch { toast.error('Could not delete!'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center"><Star className="text-orange-600" size={22}/></div>
          <div><h1 className="text-xl font-bold text-slate-800">Success Stories</h1><p className="text-slate-500 text-sm">Manage student success stories</p></div>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={18}/> Add Story
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-4 border-red-700 border-t-transparent"/></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stories.length===0
            ? <div className="col-span-3 bg-white rounded-xl border border-slate-100 text-center py-12 text-slate-400">No stories yet. Add one!</div>
            : stories.map(s=>(
              <div key={s._id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden flex-shrink-0 border-2 border-orange-200">
                    {s.imageUrl ? <img src={s.imageUrl} alt={s.name} className="w-full h-full object-cover"/> : <span className="text-2xl">👤</span>}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 text-sm">{s.name}</h3>
                    <p className="text-xs text-red-600 font-medium">{s.jobTitle}</p>
                    <p className="text-xs text-slate-400">📍 {s.location}</p>
                  </div>
                </div>
                <p className="text-slate-500 text-xs mb-4 line-clamp-2">{s.story}</p>
                <div className="flex gap-2">
                  <button onClick={()=>openEdit(s)} className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors"><Pencil size={12}/> Edit</button>
                  <button onClick={()=>{setDeleteId(s._id);setConfirmOpen(true);}} className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors"><Trash2 size={12}/> Delete</button>
                </div>
              </div>
            ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)} title={editing?'Edit Story':'Add Success Story'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
            </div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Job Title *</label>
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" required placeholder="e.g. Welder, Driver" value={form.jobTitle} onChange={e=>setForm({...form,jobTitle:e.target.value})}/>
            </div>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="e.g. Tokyo, Japan" value={form.location} onChange={e=>setForm({...form,location:e.target.value})}/>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Story *</label>
            <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" rows={4} required placeholder="Write the student's experience and success..." value={form.story} onChange={e=>setForm({...form,story:e.target.value})}/>
          </div>
          <ImageUploader label="Student Photo (optional)" hint="400 × 400 px recommended (1:1 square)" currentUrl={preview} onUpload={handleImageUpload} onRemove={handleImageRemove} uploading={uploading}/>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={()=>setModalOpen(false)} className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium text-sm">Save</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog isOpen={confirmOpen} onConfirm={handleDelete} onCancel={()=>setConfirmOpen(false)} message="Deleting this story will also remove the photo from Cloudinary. Are you sure?"/>
    </div>
  );
}
